import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { Bell, Megaphone, Wrench, Plus, Edit, Trash2, X, Check, AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-react';

const AdminCommunications = () => {
  const [activeTab, setActiveTab] = useState('announcements'); // announcements, maintenance, notifications
  const [announcements, setAnnouncements] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 'medium',
    startDate: '',
    endDate: '',
    targetAudience: 'all',
    isActive: true
  });

  const [maintenanceForm, setMaintenanceForm] = useState({
    title: '',
    message: '',
    scheduledStart: '',
    scheduledEnd: '',
    affects: 'all'
  });

  const [notificationForm, setNotificationForm] = useState({
    userId: '',
    title: '',
    message: '',
    type: 'info',
    actionUrl: ''
  });

  useEffect(() => {
    if (activeTab === 'announcements') {
      loadAnnouncements();
    } else if (activeTab === 'maintenance') {
      loadMaintenanceAlerts();
    }
  }, [activeTab]);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAnnouncements();
      if (response?.success) {
        setAnnouncements(response.announcements || []);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMaintenanceAlerts = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getMaintenanceAlerts();
      if (response?.success) {
        setMaintenanceAlerts(response.alerts || []);
      }
    } catch (error) {
      console.error('Error loading maintenance alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingAnnouncement) {
        await adminApi.updateAnnouncement(editingAnnouncement.id, announcementForm);
      } else {
        await adminApi.createAnnouncement(announcementForm);
      }
      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
      setAnnouncementForm({
        title: '',
        message: '',
        type: 'info',
        priority: 'medium',
        startDate: '',
        endDate: '',
        targetAudience: 'all',
        isActive: true
      });
      loadAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMaintenance = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.createMaintenanceAlert(maintenanceForm);
      setShowMaintenanceModal(false);
      setMaintenanceForm({
        title: '',
        message: '',
        scheduledStart: '',
        scheduledEnd: '',
        affects: 'all'
      });
      loadMaintenanceAlerts();
    } catch (error) {
      console.error('Error creating maintenance alert:', error);
      alert('Failed to create maintenance alert');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.createNotification({
        ...notificationForm,
        userId: parseInt(notificationForm.userId)
      });
      setShowNotificationModal(false);
      setNotificationForm({
        userId: '',
        title: '',
        message: '',
        type: 'info',
        actionUrl: ''
      });
      alert('Notification created successfully');
    } catch (error) {
      console.error('Error creating notification:', error);
      alert('Failed to create notification');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await adminApi.deleteAnnouncement(id);
      loadAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement');
    }
  };

  const handleDeleteMaintenance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this maintenance alert?')) return;
    
    try {
      await adminApi.deleteMaintenanceAlert(id);
      loadMaintenanceAlerts();
    } catch (error) {
      console.error('Error deleting maintenance alert:', error);
      alert('Failed to delete maintenance alert');
    }
  };

  const handleUpdateMaintenanceStatus = async (id, status) => {
    try {
      await adminApi.updateMaintenanceAlertStatus(id, status);
      loadMaintenanceAlerts();
    } catch (error) {
      console.error('Error updating maintenance status:', error);
      alert('Failed to update maintenance status');
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      priority: announcement.priority,
      startDate: announcement.startDate ? new Date(announcement.startDate).toISOString().slice(0, 16) : '',
      endDate: announcement.endDate ? new Date(announcement.endDate).toISOString().slice(0, 16) : '',
      targetAudience: announcement.targetAudience,
      isActive: announcement.isActive
    });
    setShowAnnouncementModal(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      in_progress: 'bg-orange-500',
      completed: 'bg-green-500',
      cancelled: 'bg-gray-500'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${colors[status] || 'bg-gray-500'}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'maintenance': return <Wrench className="w-5 h-5 text-orange-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Communications & Notifications</h1>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('announcements')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'announcements'
              ? 'text-yellow-500 border-b-2 border-yellow-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Megaphone className="w-5 h-5 inline mr-2" />
          Announcements
        </button>
        <button
          onClick={() => setActiveTab('maintenance')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'maintenance'
              ? 'text-yellow-500 border-b-2 border-yellow-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Wrench className="w-5 h-5 inline mr-2" />
          Maintenance Alerts
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'notifications'
              ? 'text-yellow-500 border-b-2 border-yellow-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Bell className="w-5 h-5 inline mr-2" />
          Send Notification
        </button>
      </div>

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditingAnnouncement(null);
                setAnnouncementForm({
                  title: '',
                  message: '',
                  type: 'info',
                  priority: 'medium',
                  startDate: '',
                  endDate: '',
                  targetAudience: 'all',
                  isActive: true
                });
                setShowAnnouncementModal(true);
              }}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Announcement
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No announcements found</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {announcements.map((announcement) => (
                <div key={announcement.id} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {getTypeIcon(announcement.type)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-bold text-white">{announcement.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs ${
                            announcement.isActive ? 'bg-green-500' : 'bg-gray-500'
                          } text-white`}>
                            {announcement.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            announcement.priority === 'critical' ? 'bg-red-500' :
                            announcement.priority === 'high' ? 'bg-orange-500' :
                            announcement.priority === 'medium' ? 'bg-yellow-500' : 'bg-gray-500'
                          } text-white`}>
                            {announcement.priority}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm mb-2">{announcement.message}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-400">
                          <span>Type: {announcement.type}</span>
                          <span>Audience: {announcement.targetAudience}</span>
                          {announcement.startDate && (
                            <span>Start: {new Date(announcement.startDate).toLocaleString()}</span>
                          )}
                          {announcement.endDate && (
                            <span>End: {new Date(announcement.endDate).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEditAnnouncement(announcement)}
                        className="p-2 text-blue-400 hover:text-blue-300"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowMaintenanceModal(true)}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Maintenance Alert
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            </div>
          ) : maintenanceAlerts.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No maintenance alerts found</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {maintenanceAlerts.map((alert) => (
                <div key={alert.id} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-bold text-white">{alert.title}</h3>
                        {getStatusBadge(alert.status)}
                      </div>
                      <p className="text-gray-300 text-sm mb-2">{alert.message}</p>
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                        <div>
                          <p>Scheduled: {new Date(alert.scheduledStart).toLocaleString()}</p>
                          <p>End: {new Date(alert.scheduledEnd).toLocaleString()}</p>
                        </div>
                        <div>
                          <p>Affects: {alert.affects}</p>
                          {alert.actualStart && <p>Started: {new Date(alert.actualStart).toLocaleString()}</p>}
                          {alert.actualEnd && <p>Ended: {new Date(alert.actualEnd).toLocaleString()}</p>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2 ml-4">
                      {alert.status === 'scheduled' && (
                        <>
                          <button
                            onClick={() => handleUpdateMaintenanceStatus(alert.id, 'in_progress')}
                            className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
                          >
                            Start
                          </button>
                          <button
                            onClick={() => handleUpdateMaintenanceStatus(alert.id, 'cancelled')}
                            className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {alert.status === 'in_progress' && (
                        <button
                          onClick={() => handleUpdateMaintenanceStatus(alert.id, 'completed')}
                          className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                        >
                          Complete
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteMaintenance(alert.id)}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="max-w-2xl">
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Send Notification to User</h2>
            <form onSubmit={handleCreateNotification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">User ID</label>
                <input
                  type="number"
                  value={notificationForm.userId}
                  onChange={(e) => setNotificationForm({ ...notificationForm, userId: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                <textarea
                  value={notificationForm.message}
                  onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                <select
                  value={notificationForm.type}
                  onChange={(e) => setNotificationForm({ ...notificationForm, type: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                  <option value="prize">Prize</option>
                  <option value="tournament">Tournament</option>
                  <option value="trade">Trade</option>
                  <option value="marketplace">Marketplace</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Action URL (optional)</label>
                <input
                  type="text"
                  value={notificationForm.actionUrl}
                  onChange={(e) => setNotificationForm({ ...notificationForm, actionUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  placeholder="/tournaments/123"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Notification'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </h2>
              <button
                onClick={() => {
                  setShowAnnouncementModal(false);
                  setEditingAnnouncement(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateAnnouncement} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                <textarea
                  value={announcementForm.message}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  rows={4}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                  <select
                    value={announcementForm.type}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, type: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="success">Success</option>
                    <option value="error">Error</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Priority</label>
                  <select
                    value={announcementForm.priority}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Target Audience</label>
                <select
                  value={announcementForm.targetAudience}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, targetAudience: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                >
                  <option value="all">All Users</option>
                  <option value="users">Regular Users</option>
                  <option value="admins">Admins Only</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Start Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={announcementForm.startDate}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, startDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">End Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={announcementForm.endDate}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, endDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  />
                </div>
              </div>
              {editingAnnouncement && (
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={announcementForm.isActive}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-300">Active</span>
                  </label>
                </div>
              )}
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingAnnouncement ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAnnouncementModal(false);
                    setEditingAnnouncement(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg max-w-2xl w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">New Maintenance Alert</h2>
              <button
                onClick={() => setShowMaintenanceModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateMaintenance} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={maintenanceForm.title}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                <textarea
                  value={maintenanceForm.message}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, message: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                  rows={4}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Scheduled Start</label>
                  <input
                    type="datetime-local"
                    value={maintenanceForm.scheduledStart}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, scheduledStart: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Scheduled End</label>
                  <input
                    type="datetime-local"
                    value={maintenanceForm.scheduledEnd}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, scheduledEnd: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Affects</label>
                <select
                  value={maintenanceForm.affects}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, affects: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600"
                >
                  <option value="all">All Services</option>
                  <option value="login">Login</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="tournaments">Tournaments</option>
                  <option value="payments">Payments</option>
                  <option value="api">API</option>
                </select>
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Alert'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowMaintenanceModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommunications;
